// The DLQ inbox calls the admin SPA makes: a page of parked events, a replay batch and a discard.
// A discard of an event no longer parked is Power's 404 on that route.
import { Effect, Option } from 'effect';
import { HttpBody, HttpClientRequest } from 'effect/http';
import { DlqInboxPage, DlqReplayResults } from '@finnest/power-admin-contracts';
import type {
  DlqCursorToken,
  DlqDiscardRequest,
  DlqInboxPage as DlqInboxPageType,
  DlqReplayRequest,
  DlqReplayResults as DlqReplayResultsType,
} from '@finnest/power-admin-contracts';
import { READ_BUDGET, WRITE_DEADLINE, answeredText, decoded, within } from './http.ts';
import type { OperationsOperation } from './http.ts';

const DLQ_PATH = '/power/admin/operations/dlq';

const jsonBody = (body: DlqReplayRequest | DlqDiscardRequest): HttpBody.HttpBody =>
  HttpBody.raw(JSON.stringify(body), { contentType: 'application/json' });

/** The page of parked events that starts at `cursor` (the newest failures on none). */
export const listDlq =
  (cursor: Option.Option<DlqCursorToken>): OperationsOperation<DlqInboxPageType> =>
  (apiBaseUrl) =>
    within(READ_BUDGET)(
      decoded(
        HttpClientRequest.get(`${apiBaseUrl}${DLQ_PATH}`).pipe(
          HttpClientRequest.setUrlParams(
            Option.match(cursor, { onNone: () => ({}), onSome: (token) => ({ cursor: token }) }),
          ),
        ),
        DlqInboxPage,
      ),
    );

/** Moves `request`'s events back into the outbox; one outcome per event, in request order. */
export const replayDlq =
  (request: DlqReplayRequest): OperationsOperation<DlqReplayResultsType> =>
  (apiBaseUrl) =>
    within(WRITE_DEADLINE)(
      decoded(
        HttpClientRequest.post(`${apiBaseUrl}${DLQ_PATH}/replay`, { body: jsonBody(request) }),
        DlqReplayResults,
      ),
    );

/** Deletes `request`'s event for good; `not-found` when it is no longer parked. */
export const discardDlq =
  (request: DlqDiscardRequest): OperationsOperation<void> =>
  (apiBaseUrl) =>
    within(WRITE_DEADLINE)(
      Effect.asVoid(
        answeredText(
          HttpClientRequest.post(`${apiBaseUrl}${DLQ_PATH}/discard`, { body: jsonBody(request) }),
        ),
      ),
    );
